import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import User from "lib/api/User";
import Company from "lib/api/Company";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";
import PageLayout from "components/layouts/PageLayout";
import Button from "components/common/Button";
import ButtonGroup from "components/common/ButtonGroup";
import EntityTypeahead from "components/common/EntityTypeahead";
import { filterBarStyles } from "components/common/FilterBar/styles";
import { CompanyContext } from "lib/context/CompanyContext";
import { XMarkIcon } from '@heroicons/react/24/outline';

const USER_LIST_STATE_KEY = "user_list_view_state";

function loadPersistedViewState() {
  try {
    const stored = sessionStorage.getItem(USER_LIST_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // ignore malformed/missing state
  }
  return null;
}

export default function UserList(props) {
  const { company: contextCompany } = useContext(CompanyContext);
  const isStandalone = !props.companyUuid;
  const persistedViewState = isStandalone ? loadPersistedViewState() : null;

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(1);
  const [search, setSearch] = useState(persistedViewState?.search || "");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(persistedViewState?.companyFilter || null);

  const companyUuid = companyFilter?.uuid || props.companyUuid || contextCompany.uuid;
  const usePageLayout = props.usePageLayout !== undefined ? props.usePageLayout : true;

  const [recordsPerPage, setRecordsPerPage] = useState(100);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  function buildParams(sort = { sortBy, sortDirection }) {
    const params = {};
    if (companyUuid) params["company_uuid"] = companyUuid;
    if (search) params["search"] = search;
    if (sort.sortBy) {
      params["sort_by"] = sort.sortBy;
      params["sort_direction"] = sort.sortDirection;
    }
    return Object.keys(params).length ? params : false;
  }

  function toggleSort(key) {
    const next =
      sortBy === key
        ? { sortBy: key, sortDirection: sortDirection === "asc" ? "desc" : "asc" }
        : { sortBy: key, sortDirection: "desc" };
    setSortBy(next.sortBy);
    setSortDirection(next.sortDirection);
    load(next);
  }

  function renderSortHeader(label, key) {
    return (
      <div
        onClick={() => toggleSort(key)}
        style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none" }}
        title={`Sort by ${label}`}
      >
        {label}
        {sortBy === key && (
          <span style={{ fontSize: 12 }}>{sortDirection === "asc" ? "↑" : "↓"}</span>
        )}
      </div>
    );
  }

  async function load(sort) {
    const params = buildParams(sort);
    await setLoading(true);
    let results = await User.list(0, recordsPerPage, params);
    
    await setTotal(results.total);
    await setRecords(results.data);
    await setLoading(false);
  }

  async function loadNextPage({ startIndex, stopIndex }) {
    if (loading) return;
    await setLoading(true);

    const params = buildParams();
    let response = await User.list(startIndex, Math.max(recordsPerPage, stopIndex - startIndex + 1), params);
    if (response.total !== total) {
      await setTotal(response.total);
    }
    const newData = response.data;
    setRecords(prevRecords => [...prevRecords, ...newData]);
    await setLoading(false);
  }
  
  const hasNextPage = records.length < total;
  const loadMoreRows = loading ? () => {} : loadNextPage;
  const isRowLoaded = ({ index }) => !hasNextPage || index < records.length;

  useEffect(() => {
    load();
    return ()=>{setLoading(false)};
  }, [companyUuid]);

  useEffect(() => {
    if (!isStandalone) return;
    try {
      sessionStorage.setItem(
        USER_LIST_STATE_KEY,
        JSON.stringify({ search, companyFilter })
      );
    } catch (error) {
      // ignore storage write failures (e.g. quota exceeded)
    }
  }, [isStandalone, search, companyFilter]);

  function clearFilters() {
    setSearch("");
    setCompanyFilter(null);
  }

  const content = (
    <div style={{ height: '100%', width: '100%' }}>
    <style>{filterBarStyles}</style>
    <h1>Users</h1>
    <div className="hr hr--darkest"></div>

    <div className="filter-bar">
      <div className="filter-bar__field">
        <span className="filter-bar__label">Search</span>
        <input
          className="filter-bar__input"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load()}
        />
      </div>
      {isStandalone && (
        <EntityTypeahead
          label="Company"
          placeholder="Search companies..."
          value={companyFilter}
          onSelect={setCompanyFilter}
          onClear={() => setCompanyFilter(null)}
          search={term => Company.list({ search: term, limit: 10 }).then(r => r.data)}
          formatOption={c => c.name}
        />
      )}
      <div className="filter-bar__field" style={{ flex: "0 0 auto" }}>
        <span className="filter-bar__label" style={{ visibility: "hidden" }}>&nbsp;</span>
        <div style={{ display: "flex", gap: 10 }}>
          <ButtonGroup>
            <Button onClick={() => load()}>
              Search
            </Button>
          </ButtonGroup>
          {isStandalone && (
            <button className="filter-bar__clear" onClick={clearFilters} title="Clear filters">
              <XMarkIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
            </button>
          )}
        </div>
      </div>
      {loading && (
        <div className="loading-container" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
          <div className="loading-icon"></div>
        </div>
      )}
    </div>
    <div>{records.length} of {total} loaded</div>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={total}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Table
                  headerHeight={40}
                  width={width}
                  height={ usePageLayout ? height - 120 : 600 }
                  rowCount={total}
                  rowHeight={40}
                  disableHeader={false}
                  rowGetter={({ index }) => records[index] || {}}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                >
                  <Column
                    label="Email"
                    dataKey="email"
                    width={width / 4}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return cellData;
                    }}
                  />
                  {/* <Column
                    label="Phone"
                    dataKey="mobile_phone"
                    width={width / 6}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return rowData.mobile_phone;
                    }}
                  /> */}
                  <Column
                    label="Name"
                    dataKey="last_name"
                    width={width / 4}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return `${rowData.first_name} ${rowData.last_name} `;
                    }}
                  />
                  <Column
                    label="Company"
                    dataKey="company"
                    width={width / 5}  
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      // console.log(cellData);
                      return cellData.name ? `${cellData.name}` : "";
                    }}
                  />
                  <Column
                    label="Created"
                    dataKey="created_at"
                    width={width / 5}
                    headerRenderer={() => renderSortHeader("Created", "created_at")}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading...";
                      }
                      return new Date(cellData).toLocaleString()
                    }}
                  />
                  <Column
                    label="Updated"
                    dataKey="updated_at"
                    width={width / 5}
                    headerRenderer={() => renderSortHeader("Updated", "updated_at")}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return rowData.uuid ? "" : "Loading...";
                      }
                      return new Date(cellData).toLocaleString();
                    }}
                  />
                  <Column
                    label=""
                    dataKey="uuid"
                    width={width / 4}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "";
                      }
                      return (
                        <ButtonGroup>
                        <Link className='button m-bot-0' to={`/user/${rowData.uuid}/`}>
                          View
                        </Link>
                        </ButtonGroup>
                      );
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  );

  return usePageLayout ? (
    <PageLayout>
      { content }
    </PageLayout>
  ) : content;
}
